import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import FlairPreview from './FlairPreview';
import './FlairInventory.css';

/**
 * Sorterna i visningsordning, grovt efter hur mycket de syns.
 *
 * Ordningen är fast och inte härledd ur köpen: ett inventory som hoppar runt
 * beroende på vad man råkat köpa går inte att vänja sig vid.
 */
const KIND_ORDER = ['name_color', 'title', 'title_plate', 'emblem', 'badge'];

const KIND_LABEL_KEYS = {
  name_color: 'inventory.kindNameColor',
  title: 'inventory.kindTitle',
  title_plate: 'inventory.kindTitlePlate',
  emblem: 'inventory.kindEmblem',
  badge: 'inventory.kindBadge',
};

/**
 * Spelarens ägda flair, med möjlighet att byta eller ta av.
 *
 * Bor på spelarprofilen och inte i butiken eller under /profile, av ett skäl:
 * profilhuvudet *är* det flairen ändrar. Byter du stil här ser du resultatet
 * direkt ovanför, på ditt eget namn, i skarpt läge. Butiken säljer; profilen
 * hanterar det du redan äger.
 *
 * Renderas bara för profilens ägare (`isOwnProfile` i PlayerProfile) — samma
 * regel som landsredigeringen i profilhuvudet.
 *
 * Ägandet bor för alltid i köphistoriken; equip byter bara vilket värde som
 * ligger på profilfältet. Därför går det att växla fritt hur många gånger som
 * helst, och "ta av" tappar aldrig varan.
 *
 * @param {object} props
 * @param {Function} props.onChange - Anropas efter lyckat byte så profilen kan
 *   hämta om sin flair och visa den nya direkt.
 * @returns {JSX.Element} Inventory-panelen.
 */
const FlairInventory = ({ onChange }) => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/purchases`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setPurchases((data.purchases || []).filter((p) => p.item_type === 'flair'));
      setError('');
    } catch {
      setError(t('inventory.loadError'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => { load(); }, [load]);

  /**
   * Utrusta en ägd flair, eller ta av den sort som bärs.
   *
   * @param {object} purchase Köpet som knappen hör till.
   * @param {boolean} clear True = ta av sorten i stället för att utrusta.
   */
  const equip = async (purchase, clear) => {
    setBusyId(purchase.id);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/shop/flair/equip`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: clear
          ? JSON.stringify({ kind: purchase.flair_kind, clear: true })
          : JSON.stringify({ purchase_id: purchase.id }),
      });
      if (!res.ok) throw new Error('equip failed');
      // Listan hämtas om i stället för att gissa nytt läge lokalt: servern
      // äger sanningen om vad som bärs, och en gissning som slinter gör att
      // knappen ljuger om vad man har på sig.
      await load();
      if (onChange) onChange();
    } catch {
      setError(t('inventory.equipError'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="inv-empty">{t('inventory.loading')}</div>;

  if (!purchases.length) {
    return (
      <div className="inv-empty">
        <p>{t('inventory.empty')}</p>
        <a className="inv-shop-link" href="/shop">{t('inventory.toShop')}</a>
      </div>
    );
  }

  const groups = KIND_ORDER
    .map((kind) => [kind, purchases.filter((p) => p.flair_kind === kind)])
    .filter(([, rows]) => rows.length > 0);

  return (
    <div className="flair-inventory">
      {error && <div className="inv-error">{error}</div>}
      {groups.map(([kind, rows]) => (
        <section key={kind} className="inv-group">
          <h3 className="inv-group-name">
            {t(KIND_LABEL_KEYS[kind])}
            <span className="inv-group-count">{rows.length}</span>
          </h3>
          <div className="inv-items">
            {rows.map((p) => (
              <div
                key={p.id}
                className={`inv-item${p.is_equipped ? ' is-equipped' : ''}`}
              >
                <div className="inv-preview">
                  <FlairPreview
                    kind={p.flair_kind}
                    value={p.flair_value}
                    name={p.display_name}
                    ownName={user?.steam_username || user?.username || 'DittNamn'}
                  />
                </div>
                <span className="inv-item-name">{p.display_name}</span>
                {p.is_equipped && (
                  <span className="inv-equipped-tag">{t('inventory.equipped')}</span>
                )}
                <button
                  type="button"
                  className={p.is_equipped ? 'inv-btn inv-btn-off' : 'inv-btn'}
                  disabled={busyId === p.id}
                  onClick={() => equip(p, Boolean(p.is_equipped))}
                >
                  {p.is_equipped ? t('inventory.unequip') : t('inventory.equip')}
                </button>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default FlairInventory;
